import React, { useMemo, useCallback, useEffect, useState } from "react"
import { Stack, Text, Link, IconButton, Divider } from "@chakra-ui/react"

import { Xmark } from "component/Icon"
import { MotionVStack, MotionStack } from "../"
import { XState } from "container/xstate"
import { User } from "container/user"
import { BIG_ZERO } from "constant/number"
import { formatNumberWithPrecision } from "util/format"
import { TxButton } from "../TxButton"
import { getDate, formatTime, getDuration } from "util/time"
import { CountDown } from "../CountDown"
import { CoolDownStatus, Staking } from "../../../../container/staking"
import { useToken } from "../../../../hook/useToken"
import { CONTRACT_ADDRESS } from "../../../../container/contract"

const Reminders = {
    [CoolDownStatus.init]: "Before unstaking, you'll need to initiate cooldown and wait for a period of time.",
    [CoolDownStatus.onGoing]: "Note that if you stake again during this period, the cooldown will be canceled.",
    [CoolDownStatus.readyToWithdraw]: "Cooldown complete! Ready to unstake.",
}

function formatter(ts: number): string {
    // ts should milliseconds
    const regexp = /[0-9,a-z,A-Z]+/g
    return new Date(ts).toISOString().match(regexp)!.join("")
}

function getCalenderLink(ts: number = 1616987074) {
    const _ts = ts * 1000
    const title = "Unstake Your Perp Token"
    const details = "For+details,+link+here:+https://staking.perp.exchange"
    const dates = `${formatter(_ts)}/${formatter(_ts + 60 * 60 * 1000)}`
    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&sf=true&output=xml`
}

export function UnStake() {
    const {
        actions: { xClose },
    } = XState.useContainer()
    const {
        state: { address },
    } = User.useContainer()
    const {
        unstake,
        withdraw,
        withdrawPendingBalance,
        withdrawTime: { expirationTimestamp },
        getWithdrawableTimestamp,
        cooldownStatus,
        setCoolDownStatus,
        coolDownPeriod,
    } = Staking.useContainer()
    const { balance: stakingBalance } = useToken(CONTRACT_ADDRESS.STAKE_PERP_TOKEN, 18)
    const [remainingTime, setRemainingTime] = useState(-1)

    useEffect(() => {
        async function getRemainTime() {
            const withdrawTime = await getWithdrawableTimestamp(address)
            setRemainingTime(withdrawTime.secondsUnit)
        }
        if (cooldownStatus === CoolDownStatus.onGoing) {
            getRemainTime()
        }
    }, [address, cooldownStatus, getWithdrawableTimestamp])

    const handleClick = useCallback(() => {
        if (cooldownStatus === CoolDownStatus.readyToWithdraw) {
            withdraw(xClose)
        } else if (cooldownStatus === CoolDownStatus.init) {
            unstake()
        }
    }, [cooldownStatus, withdraw, xClose, unstake])

    const canInitiateCoolDown = useMemo(() => {
        return stakingBalance.gt(BIG_ZERO)
    }, [stakingBalance])

    return (
        <MotionVStack overflow="hidden" w="100%" spacing={5} p={7} pt={10} pb={20} position="relative">
            <IconButton
                onClick={() => xClose()}
                position="absolute"
                top={3}
                left={3}
                colorScheme="gray"
                size="xs"
                aria-label="Search database"
                borderRadius={"full"}
                icon={<Xmark boxSize="16px" />}
            />

            <MotionStack w="100%" spacing={5}>
                <Stack width="100%" spacing={0}>
                    <Text color="gray.400" fontSize="xs" fontWeight="bold">
                        Cooldown Amount
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold">
                        {formatNumberWithPrecision(withdrawPendingBalance)}
                    </Text>
                </Stack>
                <Stack width="100%" spacing={0}>
                    <Text color="gray.400" fontSize="xs" fontWeight="bold">
                        Time Left
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold">
                        {cooldownStatus === CoolDownStatus.init && formatTime(getDuration(coolDownPeriod))}
                        {cooldownStatus === CoolDownStatus.onGoing && (
                            <CountDown
                                key={remainingTime}
                                startTime={remainingTime}
                                onTerminate={() => setCoolDownStatus(CoolDownStatus.readyToWithdraw)}
                            />
                        )}
                        {cooldownStatus === CoolDownStatus.readyToWithdraw && formatTime([0, 0, 0, 0])}
                    </Text>
                </Stack>
            </MotionStack>
            <MotionVStack spacing={5} w="100%">
                {cooldownStatus !== CoolDownStatus.onGoing && (
                    <TxButton
                        onClick={handleClick}
                        colorScheme={cooldownStatus === CoolDownStatus.readyToWithdraw ? "green" : "perpetual"}
                        {...(cooldownStatus === CoolDownStatus.init && { disabled: !canInitiateCoolDown })}
                    >
                        {cooldownStatus === CoolDownStatus.readyToWithdraw ? "Unstake Now" : "Initiate Cooldown"}
                    </TxButton>
                )}
                {cooldownStatus === CoolDownStatus.onGoing && (
                    <>
                        <Text color="#808080">
                            Cooldown started. Please check back at {getDate(expirationTimestamp)}.{" "}
                            <Link color="perpetual.500" href={getCalenderLink(expirationTimestamp)} isExternal>
                                Add to calendar
                            </Link>
                        </Text>
                        <Divider />
                    </>
                )}
                <Text color="#808080" fontSize="sm">
                    {Reminders[cooldownStatus]}
                </Text>
            </MotionVStack>
        </MotionVStack>
    )
}
